import { useTranslation } from 'react-i18next';
import { Loader2, Settings } from 'lucide-react';
import { QrCode, Xmark } from 'iconoir-react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { useGetIntegrationConnections } from 'hooks/use-get-integration-connections';
import type { ActiveIntegrationConnection } from 'services/integration-service';
import { isIntegrationConnected } from './provider-utils';
import { getWhatsAppChatUrl } from './whatsapp-config';
import { getTeamsChatUrl } from './teams-config';

type TelegramBotsListModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  channelId?: string;
  onStartChatting?: (connection: ActiveIntegrationConnection) => void;
  onConnect?: (connection: ActiveIntegrationConnection) => void;
  onDisconnect?: (connection: ActiveIntegrationConnection) => void;
  onAddBot?: () => void;
  busyConnectionId?: string | null;
};

const getBotDisplayName = (connection: ActiveIntegrationConnection) =>
  connection.bot_name ||
  connection.external_refs?.teams?.bot_name ||
  connection.external_refs?.telegram?.bot_name ||
  connection.external_refs?.whatsapp?.verified_name ||
  connection.display_name ||
  null;

const getBotUsername = (connection: ActiveIntegrationConnection) => {
  if (connection.external_refs?.teams?.azure_bot_resource_name) {
    return connection.external_refs.teams.azure_bot_resource_name;
  }
  if (connection.external_refs?.teams?.microsoft_app_id) {
    return `App ID: ${connection.external_refs.teams.microsoft_app_id}`;
  }

  const username =
    connection.bot_username ||
    connection.external_refs?.telegram?.bot_username ||
    (typeof connection.external?.bot_username === 'string'
      ? connection.external.bot_username
      : null);

  if (typeof username === 'string' && username.trim()) {
    return `@${username.replace(/^@/, '')}`;
  }

  return connection.external_refs?.whatsapp?.display_phone_number || null;
};

const getBotConnectionUrl = (connection: ActiveIntegrationConnection) =>
  connection.connection_url ||
  connection.external_refs?.teams?.bot_link ||
  connection.external_refs?.telegram?.bot_link ||
  getWhatsAppChatUrl(connection) ||
  getTeamsChatUrl(connection);

export const TelegramBotsListModal = ({
  open,
  onOpenChange,
  agentId,
  channelId = 'telegram',
  onStartChatting,
  onConnect,
  onDisconnect,
  onAddBot,
  busyConnectionId = null,
}: TelegramBotsListModalProps) => {
  const { t } = useTranslation();
  const isWhatsApp = channelId === 'whatsapp';
  const isTeams = channelId === 'teams' || channelId === 'microsoft-teams';
  const {
    data: connections = [],
    isLoading,
    isError,
    refetch,
  } = useGetIntegrationConnections(
    { channel_id: channelId, agent_id: agentId },
    open && !!agentId
  );

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent
        hideClose
        className="flex max-h-[90vh] w-full max-w-[565px] flex-col gap-6 overflow-hidden rounded-xl border-0 bg-white p-4 shadow-[0_24px_80px_rgba(15,23,42,0.18)] dark:bg-background sm:p-8"
      >
        <button
          type="button"
          aria-label={t('CLOSE') || 'Close'}
          onClick={handleClose}
          className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center text-[#6D6D6D] focus:outline-none dark:text-true-white"
        >
          <Xmark width={14} height={14} />
        </button>

        <div className="flex items-start gap-3 pr-8">
          <img
            src={
              isWhatsApp
                ? '/assets/images/agents/whatsapp-brand.svg'
                : isTeams
                  ? '/assets/images/agents/teams.png'
                  : '/assets/images/agents/telegram-brand.svg'
            }
            alt=""
            className="size-10 shrink-0 rounded-full object-cover"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <DialogTitle className="font-sans text-2xl font-semibold not-italic leading-[120%] tracking-normal text-grey-700 dark:text-true-white">
              {isWhatsApp
                ? t('WHATSAPP_CONNECTIONS', { defaultValue: 'WhatsApp connections' })
                : isTeams
                  ? t('TEAMS_CONNECTIONS', { defaultValue: 'Teams connections' })
                  : t('TELEGRAM_BOTS', { defaultValue: 'Telegram bots' })}
            </DialogTitle>
            <p className="font-sans text-sm font-normal not-italic leading-[160%] text-grey-500 dark:text-grey-400">
              {isWhatsApp
                ? t('WHATSAPP_CONNECTIONS_LIST_DESCRIPTION', {
                    defaultValue: 'Manage your connected WhatsApp numbers.',
                  })
                : isTeams
                  ? t('TEAMS_CONNECTIONS_LIST_DESCRIPTION', {
                      defaultValue: 'Manage your connected Microsoft Teams bots.',
                    })
                  : t('TELEGRAM_BOTS_LIST_DESCRIPTION', {
                      defaultValue: 'Manage your connected Telegram bots.',
                    })}
            </p>
          </div>
          {onAddBot ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0 rounded-md border-[#e7e7e7] dark:border-border"
              onClick={onAddBot}
              aria-label={t('SETTINGS', { defaultValue: 'Settings' })}
            >
              <Settings className="h-4 w-4" aria-hidden />
            </Button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : null}

          {isError ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-[#e7e7e7] px-4 py-8 text-center dark:border-border">
              <p className="text-sm text-[#888888] dark:text-muted-foreground">
                {t(
                  isWhatsApp
                    ? 'FAILED_TO_LOAD_WHATSAPP_CONNECTIONS'
                    : isTeams
                      ? 'FAILED_TO_LOAD_TEAMS_CONNECTIONS'
                      : 'FAILED_TO_LOAD_TELEGRAM_BOTS',
                  {
                    defaultValue: isWhatsApp
                      ? 'Failed to load WhatsApp connections. Please try again.'
                      : isTeams
                        ? 'Failed to load Teams connections. Please try again.'
                        : 'Failed to load Telegram bots. Please try again.',
                  }
                )}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
                {t('RETRY', { defaultValue: 'Retry' })}
              </Button>
            </div>
          ) : null}

          {!isLoading && !isError && connections.length === 0 ? (
            <div className="rounded-lg border border-[#e7e7e7] px-4 py-8 text-center dark:border-border">
              <p className="text-sm text-[#888888] dark:text-muted-foreground">
                {t(
                  isWhatsApp
                    ? 'NO_WHATSAPP_CONNECTIONS_FOUND'
                    : isTeams
                      ? 'NO_TEAMS_CONNECTIONS_FOUND'
                      : 'NO_TELEGRAM_BOTS_FOUND',
                  {
                    defaultValue: isWhatsApp
                      ? 'No WhatsApp connections found.'
                      : isTeams
                        ? 'No Teams connections found.'
                        : 'No Telegram bots found.',
                  }
                )}
              </p>
            </div>
          ) : null}

          {!isLoading && !isError && connections.length > 0 ? (
            <div className="flex flex-col gap-2">
              {connections.map((connection) => {
                const connected = isIntegrationConnected(connection.status);
                const botUsername = getBotUsername(connection);
                const isBusy = busyConnectionId === connection.id;

                return (
                  <div
                    key={connection.id}
                    className="flex items-center gap-3 rounded-lg border border-[#e7e7e7] bg-[#FAFAFA] px-4 py-3 dark:border-border dark:bg-muted/30"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[#3d3d3d] dark:text-foreground">
                        {getBotDisplayName(connection) ||
                          (isWhatsApp
                            ? t('WHATSAPP_CONNECTION', { defaultValue: 'WhatsApp connection' })
                            : isTeams
                              ? t('TEAMS_BOT', { defaultValue: 'Teams bot' })
                              : t('TELEGRAM_BOT', { defaultValue: 'Telegram bot' }))}
                      </p>
                      {botUsername ? (
                        <p className="truncate text-xs text-[#888888] dark:text-muted-foreground">
                          {botUsername}
                        </p>
                      ) : null}
                    </div>

                    {connected && onStartChatting && getBotConnectionUrl(connection) ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0 rounded-md border-[#e7e7e7] dark:border-border"
                        onClick={() => onStartChatting(connection)}
                        aria-label={t('SCAN_TO_START_CHATTING', { defaultValue: 'Scan to start chatting' })}
                      >
                        <QrCode width={16} height={16} aria-hidden />
                      </Button>
                    ) : null}

                    {connected && onDisconnect ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 shrink-0 rounded-md border-[#DC2626] px-3 text-xs font-medium text-[#DC2626] hover:bg-[#FEF2F2] dark:border-red-500 dark:text-red-400 dark:hover:bg-red-500/10"
                        disabled={isBusy}
                        onClick={() => onDisconnect(connection)}
                      >
                        {isBusy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                        {t('DISCONNECT', { defaultValue: 'Disconnect' })}
                      </Button>
                    ) : null}

                    {!connected && onConnect ? (
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 shrink-0 rounded-md px-3 text-xs font-medium"
                        disabled={isBusy}
                        onClick={() => onConnect(connection)}
                      >
                        {isBusy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                        {t(isWhatsApp || isTeams ? 'RECONNECT' : 'CONNECT', {
                          defaultValue: isWhatsApp || isTeams ? 'Reconnect' : 'Connect',
                        })}
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
